import React from 'react';
import PropTypes from 'prop-types';

import {Box, IconButton, Typography} from 'views/components';
import {MoreHoriz} from 'views/icons';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const ScheduleEventDisplayRecurring = (props) => {
  const {events, onMenuClick} = props;

  const daysOfWeek = React.useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);
  const dayNamesByShort = {
    Sun: translate('schedulestep.Common.DayFull.Sun'),
    Mon: translate('schedulestep.Common.DayFull.Mon'),
    Tue: translate('schedulestep.Common.DayFull.Tue'),
    Wed: translate('schedulestep.Common.DayFull.Wed'),
    Thu: translate('schedulestep.Common.DayFull.Thu'),
    Fri: translate('schedulestep.Common.DayFull.Fri'),
    Sat: translate('schedulestep.Common.DayFull.Sat'),
  };

  const sortRepeats = React.useCallback((repeats = []) => {
    return [...repeats].sort((dayA, dayB) => daysOfWeek.indexOf(dayA) - daysOfWeek.indexOf(dayB));
  }, [daysOfWeek]);

  const formatRepeatTitle = React.useCallback((repeats = []) => {
    const fullDays = repeats.map((day) => dayNamesByShort[day]);

    if (!repeats.length) {
      return translate('schedulestep.ScheduleCard.CustomSchedule');
    }
    if (repeats.length === 1) {
      return fullDays[0];
    }
    if (repeats.length === 2) {
      return `${fullDays[0]} & ${fullDays[1]}`;
    }
    const first = fullDays.slice(0, -1).join(', ');
    const last = fullDays[fullDays.length - 1];
    return `${first} & ${last}`;
  }, [dayNamesByShort]);

  const groupedEvents = React.useMemo(() => {
    const groupedByRepeats = new Map();

    events.forEach((eventItem) => {
      const normalizedRepeats = sortRepeats(eventItem.repeats);
      const repeatsKey = normalizedRepeats.join('|');

      if (!groupedByRepeats.has(repeatsKey)) {
        groupedByRepeats.set(repeatsKey, {
          id: repeatsKey,
          title: formatRepeatTitle(normalizedRepeats),
          careTimes: [],
        });
      }

      groupedByRepeats.get(repeatsKey).careTimes.push({
        id: eventItem.id,
        label: eventItem.label,
        startTime: eventItem.startTime,
        endTime: eventItem.endTime,
      });
    });

    return Array.from(groupedByRepeats.values());
  }, [events, formatRepeatTitle, sortRepeats]);

  const getFormattedTime = React.useCallback((value) => {
    const parsed = moment(value);
    return parsed.format('h:mm A');
  }, []);

  return (
    <>
      {
        groupedEvents.map((groupedEvent) => (
          <Box
            key={groupedEvent.id}
            sx={{
              border: '1px dashed #C5BBB9',
              borderRadius: '24px',
              p: '32px',
              backgroundColor: 'white',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
              <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
                {groupedEvent.title}
                <Box component='span' sx={{color: '#2D2925A3'}}>
                  <Translate expression={'schedulestep.ScheduleEventDisplayRecurring.RecurringSuffix'} />
                </Box>
              </Typography>
              <IconButton
                onClick={(event) => onMenuClick(event, groupedEvent.careTimes.map((careTime) => ({id: careTime.id, label: careTime.label})))}
                sx={{border: '1px solid #C5BBB9', borderRadius: '8px', p: '4px'}}
              >
                <MoreHoriz sx={{fontSize: 24}} />
              </IconButton>
            </Box>

            <Box sx={{display: 'flex', gap: '72px', flexWrap: 'wrap'}}>
              {
                groupedEvent.careTimes.map((careTime) => (
                  <Box key={careTime.id}>
                    <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', textTransform: 'capitalize'}}>
                      {careTime.label}
                    </Typography>
                    <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925A3', lineHeight: '150%'}}>
                      {`${getFormattedTime(careTime.startTime)} - ${getFormattedTime(careTime.endTime)}`}
                    </Typography>
                  </Box>
                ))
              }
            </Box>
          </Box>
        ))
      }
    </>
  );
};

ScheduleEventDisplayRecurring.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    repeats: PropTypes.arrayOf(PropTypes.string),
  })),
  onMenuClick: PropTypes.func,
};

export default React.memo(ScheduleEventDisplayRecurring);
